import { useEffect } from 'react';

import BrowserHistory from '../components/utils/BrowserHistory';
import RumClient from './RumClient';

/** A page whose first load we time. Must keep in sync with the metric definitions in CDK. */
type TrackedPage = 'view' | 'home';

// Becomes true once this tab has sent its event or can no longer send one.
let didReportPageLoadTime = false;

// When the user moves to another page in-app, then stop timing for this tab.
// Redirects use REPLACE, so they keep the landing page; clicks (PUSH) and back/forward (POP) do not.
// View-page tab switches also use REPLACE, so the view page calls cancelPageLoadTimeTracking itself.
BrowserHistory.listen((_location, action) => {
  if (action !== 'REPLACE') {
    didReportPageLoadTime = true;
  }
});

/**
 * Stops timing for this browser tab so later reportPageLoadTime calls send nothing, for use when the
 * user sees something other than the tracked content first.
 */
export function cancelPageLoadTimeTracking(): void {
  didReportPageLoadTime = true;
}

/** Sends the time from opening the URL until the given page shows its main content, once per browser tab. */
export function reportPageLoadTime(page: TrackedPage): void {
  // Only the first ready page of the tab counts, and only while the user is still on it.
  if (didReportPageLoadTime) {
    return;
  }
  didReportPageLoadTime = true;

  // The browser starts performance.now() when the user opens the URL.
  RumClient.recordEvent('page_ready', { page, durationMs: Math.round(performance.now()) });
}

/** Reports the page load time automatically once the component is ready, by calling reportPageLoadTime for you. */
export function useReportPageLoadTime(page: TrackedPage, isReady: boolean): void {
  // When the main content has rendered on screen, then report the page as ready.
  useEffect(() => {
    if (!isReady) {
      return;
    }

    reportPageLoadTime(page);
  }, [page, isReady]);
}
